import './consent/consent-bootstrap';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Router } from 'wouter';
import { ThemeProvider } from './components/theme-provider';
import { ConsentProvider } from './consent/ConsentContext';
import i18n, { langPrefixOf, ensureLanguage } from './lib/i18n';
import App from './App.tsx';
import './index.css';

const container = document.getElementById('root')!;
// Localized public pages live under `/uk/*`; set the wouter base so the whole
// route tree (and every <Link>) is relative to it, with no per-route prefixing.
const routerBase = langPrefixOf(window.location.pathname) ? '/uk' : '';
const tree = (
  <StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ConsentProvider>
        <Router base={routerBase}>
          <App />
        </Router>
      </ConsentProvider>
    </ThemeProvider>
  </StrictMode>
);

// The prerendered (SSG) marketing HTML inside #root is a build-time headless-
// browser (createRoot) snapshot, NOT React SSR output, so it lacks the internal
// markers hydrateRoot needs (Suspense boundary comments, matching base-ui useId/
// aria state applied via effects). hydrateRoot therefore always mismatched and
// React discarded + re-rendered the whole tree anyway — a TBT cost plus a
// "Hydration failed" console error on every public page. Mount with createRoot
// instead: it re-renders the identical tree over the already-painted prerendered
// DOM (committed synchronously → no visible flash), skipping the doomed
// hydration walk. Crawlers still receive the prerendered HTML for SEO.
// The non-default (uk) locale is code-split out of the entry chunk. Load the
// active language before the first paint so a Ukrainian page/visitor renders in
// Ukrainian with no flash of English; render either way if the chunk fails
// (i18next falls back to the bundled EN).
const renderApp = () => createRoot(container).render(tree);
// Key off i18n.language (the requested lng, e.g. 'uk'), NOT resolvedLanguage —
// the latter falls back to 'en' until the uk bundle is present, which would
// skip the very load we need.
ensureLanguage(i18n.language).then(renderApp, renderApp);
